import { useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import * as VisuallyHidden from "@radix-ui/react-visually-hidden";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Wallet, Sparkles, ChevronRight, ChevronLeft, Check, PiggyBank } from "lucide-react";
import { toast } from "sonner";

const STEPS = ["welcome", "currency", "first", "done"];

export default function OnboardingWizard({ open, onClose }) {
  const { user, refreshMe } = useAuth();
  const [step, setStep] = useState(0);
  const [currency, setCurrency] = useState(user?.currency || "USD");
  const [ft, setFt] = useState({ type: "expense", amount: "", category: "Food", note: "" });
  const [seedSample, setSeedSample] = useState(false);
  const [saving, setSaving] = useState(false);

  const next = () => setStep((s) => Math.min(s + 1, STEPS.length - 1));
  const prev = () => setStep((s) => Math.max(s - 1, 0));

  const finish = async () => {
    setSaving(true);
    try {
      const payload = { currency, seed_sample: seedSample };
      if (!seedSample && ft.amount) {
        payload.first_transaction = {
          type: ft.type,
          amount: Number(ft.amount),
          category: ft.category,
          note: ft.note,
        };
      }
      await api.post("/user/onboarding/complete", payload);
      await refreshMe();
      toast.success("You're all set!");
      onClose?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not complete setup");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent data-testid="onboarding-wizard" className="max-w-md">
        <VisuallyHidden.Root>
          <DialogTitle>Welcome to Accountrix</DialogTitle>
          <DialogDescription>Quick 3-step setup for your account.</DialogDescription>
        </VisuallyHidden.Root>
        <div className="pt-2">
          <div className="flex gap-1 mb-6">
            {STEPS.map((s, i) => (
              <div
                key={s.key || s.title || `step-${i}`}
                className={`h-1 flex-1 rounded-full transition-colors ${i <= step ? "bg-[var(--brand)]" : "bg-[var(--track)]"}`}
              />
            ))}
          </div>

          {step === 0 && (
            <div data-testid="onboarding-welcome" className="space-y-4">
              <div className="w-12 h-12 rounded-md bg-[var(--brand)] flex items-center justify-center">
                <Wallet className="w-6 h-6 text-[#FDFCF8]" />
              </div>
              <div>
                <div className="text-xs tracking-[0.2em] uppercase text-[var(--muted-text)]">Welcome</div>
                <h2 className="font-display text-2xl mt-1">Nice to meet you, {user?.name?.split(" ")[0]}</h2>
                <p className="text-sm text-[var(--muted-text)] mt-2">
                  Let&apos;s take 30 seconds to set up Accountrix for how you like to track money.
                </p>
              </div>
            </div>
          )}

          {step === 1 && (
            <div data-testid="onboarding-currency" className="space-y-4">
              <div>
                <div className="text-xs tracking-[0.2em] uppercase text-[var(--muted-text)]">Currency</div>
                <h2 className="font-display text-2xl mt-1">What currency do you think in?</h2>
                <p className="text-sm text-[var(--muted-text)] mt-2">
                  You can switch anytime from the sidebar, and Accountrix will convert everything for you.
                </p>
              </div>
              <div className="space-y-2">
                <Label>Primary currency</Label>
                <Select value={currency} onValueChange={setCurrency}>
                  <SelectTrigger data-testid="onboarding-currency-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="USD">USD — US Dollar</SelectItem>
                    <SelectItem value="NGN">NGN — Nigerian Naira</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {step === 2 && (
            <div data-testid="onboarding-first" className="space-y-4">
              <div>
                <div className="text-xs tracking-[0.2em] uppercase text-[var(--muted-text)]">Kickstart</div>
                <h2 className="font-display text-2xl mt-1">Add your first transaction</h2>
                <p className="text-sm text-[var(--muted-text)] mt-2">
                  Or skip and load sample data so charts aren&apos;t empty on your first login.
                </p>
              </div>

              <button
                type="button"
                data-testid="onboarding-seed"
                onClick={() => setSeedSample((s) => !s)}
                className={`w-full text-left p-3 rounded-md border transition-colors ${seedSample ? "border-[var(--brand)] bg-[var(--hover)]" : "border-[var(--border)]"}`}
              >
                <div className="flex items-start gap-3">
                  <Sparkles className="w-4 h-4 text-[var(--brand)] mt-0.5" />
                  <div className="flex-1">
                    <div className="text-sm font-medium">Load 2 months of sample data</div>
                    <div className="text-xs text-[var(--muted-text)] mt-0.5">
                      Two months of realistic transactions, 3 budgets, a savings goal, and one stock. You can delete it later.
                    </div>
                  </div>
                  {seedSample && <Check className="w-4 h-4 text-[var(--income)]" />}
                </div>
              </button>

              {!seedSample && (
                <div className="space-y-3 pt-2 border-t border-[var(--border)]">
                  <div className="text-xs tracking-[0.2em] uppercase text-[var(--muted-text)]">Or add one now</div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">Type</Label>
                      <Select value={ft.type} onValueChange={(v) => setFt({ ...ft, type: v })}>
                        <SelectTrigger data-testid="onboarding-ft-type"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="expense">Expense</SelectItem>
                          <SelectItem value="income">Income</SelectItem>
                          <SelectItem value="saving">Saving</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Amount</Label>
                      <Input
                        data-testid="onboarding-ft-amount"
                        type="number" step="0.01"
                        value={ft.amount}
                        onChange={(e) => setFt({ ...ft, amount: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Category</Label>
                    <Input
                      data-testid="onboarding-ft-category"
                      value={ft.category}
                      onChange={(e) => setFt({ ...ft, category: e.target.value })}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Note (optional)</Label>
                    <Input
                      data-testid="onboarding-ft-note"
                      value={ft.note}
                      onChange={(e) => setFt({ ...ft, note: e.target.value })}
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {step === 3 && (
            <div data-testid="onboarding-done" className="space-y-4 text-center py-4">
              <div className="w-14 h-14 rounded-full bg-[#EAF4EE] mx-auto flex items-center justify-center">
                <PiggyBank className="w-7 h-7 text-[var(--income)]" />
              </div>
              <h2 className="font-display text-2xl">You&apos;re ready</h2>
              <p className="text-sm text-[var(--muted-text)]">
                Accountrix is set up in <b>{currency}</b>
                {seedSample && " with sample data ready to explore"}
                {!seedSample && ft.amount && " and your first transaction logged"}.
                Explore the dashboard, set budgets, or connect a bank next.
              </p>
            </div>
          )}

          <div className="flex justify-between items-center mt-8">
            <Button
              data-testid="onboarding-back"
              variant="ghost"
              onClick={prev}
              disabled={step === 0}
              className={step === 0 ? "invisible" : ""}
            >
              <ChevronLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            {step < STEPS.length - 1 ? (
              <Button
                data-testid="onboarding-next"
                onClick={next}
                className="rounded-full btn-brand"
              >
                Continue <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            ) : (
              <Button
                data-testid="onboarding-finish"
                onClick={finish}
                disabled={saving}
                className="rounded-full btn-brand"
              >
                {saving ? "Setting up..." : "Take me in"}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
